import { useEffect, useState } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * PortalSectionsDnd — drag-reorderable container for the portal's top-level
 * foldable sections. The client-visible order is persisted to localStorage
 * per customer so each client can organise the view however suits them.
 *
 * Usage:
 *   <PortalSectionsDnd
 *     storageKey={`hv:portal-order:${customerId}`}
 *     sections={[ { id, render: () => <YourBlock /> }, ... ]}
 *     enabled={isClient}
 *   />
 *
 * Staff-preview renders the sections in natural order with no grip handle
 * since reorders should only be owned by the client.
 */
export default function PortalSectionsDnd({ sections, storageKey, enabled = true }) {
  const defaultOrder = sections.map((s) => s.id);
  const [order, setOrder] = useState(() => {
    if (!enabled) return defaultOrder;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved)) {
          // Merge: keep saved order for known IDs, append any new sections.
          const known = new Set(defaultOrder);
          const filtered = saved.filter((id) => known.has(id));
          const missing = defaultOrder.filter((id) => !filtered.includes(id));
          return [...filtered, ...missing];
        }
      }
    } catch {}
    return defaultOrder;
  });

  // Persist on change
  useEffect(() => {
    if (!enabled) return;
    try { localStorage.setItem(storageKey, JSON.stringify(order)); } catch {}
  }, [order, storageKey, enabled]);

  // If new sections appear (e.g. after feature flag flip), merge them in.
  useEffect(() => {
    const known = new Set(order);
    const missing = sections.map((s) => s.id).filter((id) => !known.has(id));
    if (missing.length) setOrder((cur) => [...cur, ...missing]);
    // Drop stale IDs too.
    const valid = new Set(sections.map((s) => s.id));
    const stale = order.filter((id) => !valid.has(id));
    if (stale.length) setOrder((cur) => cur.filter((id) => valid.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.map((s) => s.id).join("|")]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const onDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    setOrder((cur) => {
      const oldIdx = cur.indexOf(active.id);
      const newIdx = cur.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return cur;
      return arrayMove(cur, oldIdx, newIdx);
    });
  };

  const byId = Object.fromEntries(sections.map((s) => [s.id, s]));
  const ordered = order.map((id) => byId[id]).filter(Boolean);

  if (!enabled) {
    return (
      <div className="space-y-3" data-testid="portal-sections-static">
        {ordered.map((s) => <div key={s.id}>{s.render()}</div>)}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <div className="space-y-3" data-testid="portal-sections-dnd">
          {ordered.map((s) => <SortableItem key={s.id} id={s.id}>{s.render()}</SortableItem>)}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group/drag"
      data-testid={`portal-section-wrap-${id}`}
    >
      {/* Grip handle — only on ≥md viewport and only visible on hover */}
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="hidden md:flex items-center justify-center absolute -left-7 top-3 w-6 h-6 rounded text-[#647087] hover:text-white hover:bg-white/10 cursor-grab active:cursor-grabbing opacity-0 group-hover/drag:opacity-100 transition"
        title="Drag to reorder"
        data-testid={`portal-section-grip-${id}`}
      ><GripVertical className="w-4 h-4" /></button>
      {children}
    </div>
  );
}
